<!-- Licensed to the Apache Software Foundation (ASF) under one or more contributor license agreements. -->
<template>
  <div class="form-layout" v-ctrl-enter="handleSubmit">
    <a-spin :spinning="loading">
      <a-alert
        type="warning"
        show-icon
        :message="$t('message.migrate.account.project.resources.warning')"
        style="margin-bottom: 16px" />

      <a-form
        :ref="formRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        @finish="handleSubmit">
        <a-form-item :label="$t('label.account')">
          <a-input :value="resource.name" disabled />
        </a-form-item>

        <a-form-item name="projectid" :label="$t('label.project')">
          <a-select
            v-model:value="form.projectid"
            :loading="projectsLoading"
            showSearch
            optionFilterProp="label"
            :placeholder="$t('message.select.project.for.resource.migration')">
            <a-select-option
              v-for="project in projects"
              :key="project.id"
              :value="project.id"
              :label="project.name">
              {{ project.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-button
          v-if="canCreateProject && form.direction === 'toproject' && !showCreateProject"
          type="link"
          style="padding-left: 0; margin-top: -12px; margin-bottom: 12px"
          @click="showCreateProject = true">
          <plus-outlined />
          {{ $t('label.create.project.from.migration') }}
        </a-button>

        <a-card
          v-if="showCreateProject && form.direction === 'toproject'"
          size="small"
          :title="$t('label.create.project.from.migration')"
          style="margin-bottom: 16px">
          <a-form-item :label="$t('label.name')" required>
            <a-input
              v-model:value="newProject.name"
              :disabled="projectCreating"
              :placeholder="$t('message.enter.new.project.name')" />
          </a-form-item>
          <a-form-item :label="$t('label.displaytext')">
            <a-input
              v-model:value="newProject.displaytext"
              :disabled="projectCreating"
              :placeholder="$t('message.enter.new.project.displaytext')" />
          </a-form-item>
          <div class="create-project-actions">
            <a-button :disabled="projectCreating" @click="cancelCreateProject">
              {{ $t('label.cancel') }}
            </a-button>
            <a-button type="primary" :loading="projectCreating" @click="createProject">
              {{ $t('label.create.project') }}
            </a-button>
          </div>
        </a-card>

        <a-form-item name="direction" :label="$t('label.direction')">
          <a-radio-group v-model:value="form.direction" buttonStyle="solid">
            <a-radio-button value="toproject">
              {{ $t('label.account.to.project') }}
            </a-radio-button>
            <a-radio-button value="toaccount">
              {{ $t('label.project.to.account') }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item name="confirmed">
          <a-checkbox v-model:checked="form.confirmed">
            {{ $t('message.confirm.account.project.resource.migration') }}
          </a-checkbox>
        </a-form-item>

        <div :span="24" class="action-button">
          <a-button @click="closeAction">{{ $t('label.cancel') }}</a-button>
          <a-button :loading="loading" ref="submit" type="primary" danger @click="handleSubmit">
            {{ $t('label.move.resources') }}
          </a-button>
        </div>
      </a-form>
    </a-spin>
  </div>
</template>

<script>
import { reactive, ref, toRaw } from 'vue'
import { getAPI } from '@/api'
import { PlusOutlined } from '@ant-design/icons-vue'

export default {
  name: 'MigrateAccountProjectResources',
  components: {
    PlusOutlined
  },
  props: {
    resource: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      projectsLoading: false,
      projects: [],
      showCreateProject: false,
      projectCreating: false,
      newProject: {
        name: '',
        displaytext: ''
      }
    }
  },
  created () {
    this.initForm()
    this.fetchProjects()
  },
  computed: {
    canCreateProject () {
      return 'createProject' in this.$store.getters.apis
    }
  },
  methods: {
    initForm () {
      this.formRef = ref()
      this.form = reactive({
        projectid: undefined,
        direction: 'toproject',
        confirmed: false
      })
      this.rules = reactive({
        projectid: [{ required: true, message: this.$t('message.error.select') }],
        direction: [{ required: true, message: this.$t('message.error.select') }],
        confirmed: [{
          validator: (_rule, value) => value
            ? Promise.resolve()
            : Promise.reject(new Error(this.$t('message.confirm.account.project.resource.migration')))
        }]
      })
    },
    fetchProjects (preferredProjectId, preferredProjectName) {
      this.projectsLoading = true
      return getAPI('listProjects', {
        domainid: this.resource.domainid,
        state: 'Active',
        listall: true,
        page: 1,
        pagesize: 500
      }).then(response => {
        this.projects = response.listprojectsresponse.project || []
        const project = this.projects.find(item =>
          (preferredProjectId && item.id === preferredProjectId) ||
          (preferredProjectName && item.name === preferredProjectName))
        if (project) {
          this.form.projectid = project.id
        }
      }).catch(error => {
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: (error.response && error.response.headers && error.response.headers['x-description']) || error.message,
          duration: 0
        })
      }).finally(() => {
        this.projectsLoading = false
      })
    },
    cancelCreateProject () {
      this.showCreateProject = false
      this.newProject.name = ''
      this.newProject.displaytext = ''
    },
    createProject (event) {
      if (event) event.preventDefault()
      if (this.projectCreating) return

      const name = this.newProject.name.trim()
      if (!name) {
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: this.$t('message.enter.new.project.name')
        })
        return
      }

      this.projectCreating = true
      getAPI('createProject', {
        name,
        displaytext: this.newProject.displaytext.trim() || name,
        account: this.resource.name,
        domainid: this.resource.domainid
      }).then(response => {
        const commandResponse = response.createprojectresponse || {}
        this.$pollJob({
          jobId: commandResponse.jobid,
          title: this.$t('label.create.project'),
          description: name,
          loadingMessage: this.$t('message.creating.project'),
          successMessage: this.$t('message.success.create.project'),
          errorMessage: this.$t('message.failed.create.project'),
          catchMessage: this.$t('error.fetching.async.job.result'),
          successMethod: result => {
            const jobResult = result.jobresult || {}
            const createdProject = Array.isArray(jobResult.project)
              ? jobResult.project[0]
              : (jobResult.project || jobResult)
            this.fetchProjects(createdProject?.id, name)
            this.projectCreating = false
            this.showCreateProject = false
            this.newProject.name = ''
            this.newProject.displaytext = ''
          },
          errorMethod: () => {
            this.projectCreating = false
          },
          catchMethod: () => {
            this.projectCreating = false
          }
        })
      }).catch(error => {
        this.projectCreating = false
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: (error.response && error.response.headers && error.response.headers['x-description']) || error.message,
          duration: 0
        })
      })
    },
    handleSubmit (event) {
      if (event) event.preventDefault()
      if (this.loading) return

      this.formRef.value.validate().then(() => {
        const values = toRaw(this.form)
        this.loading = true
        getAPI('migrateAccountProjectResources', {
          accountid: this.resource.id,
          projectid: values.projectid,
          direction: values.direction
        }).then(response => {
          const commandResponse = response.migrateaccountprojectresourcesresponse || {}
          this.$pollJob({
            jobId: commandResponse.jobid,
            title: this.$t('label.move.account.project.resources'),
            description: this.resource.name,
            loadingMessage: this.$t('message.migrating.account.project.resources'),
            successMessage: this.$t('message.success.account.project.resource.migration.started'),
            errorMessage: this.$t('message.failed.account.project.resource.migration'),
            catchMessage: this.$t('error.fetching.async.job.result'),
            successMethod: result => {
              const jobResult = result.jobresult || {}
              const migrationResult = jobResult.migration || jobResult.null || jobResult
              const count = Number(migrationResult.total) || 0
              this.$emit('refresh-data')
              this.$notification.success({
                message: this.$t('label.move.resources'),
                description: this.$t('message.success.account.project.resource.migration', { count })
              })
            }
          })
          this.closeAction()
        }).catch(error => {
          this.$notification.error({
            message: this.$t('message.request.failed'),
            description: (error.response && error.response.headers && error.response.headers['x-description']) || error.message,
            duration: 0
          })
        }).finally(() => {
          this.loading = false
        })
      }).catch(error => {
        if (error.errorFields && error.errorFields.length > 0) {
          this.formRef.value.scrollToField(error.errorFields[0].name)
        }
      })
    },
    closeAction () {
      this.$emit('close-action')
    }
  }
}
</script>

<style scoped lang="less">
  .form-layout {
    width: 80vw;
    @media (min-width: 600px) {
      width: 520px;
    }
  }
  .create-project-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
</style>
